'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetMarketRow } from '@morfi/backend-sdk-v2/client';
import type { MarketStatusString } from '@morfi/contract-sdk';
import { isMarketTradeable, isValidMarketStatus } from '@morfi/contract-sdk';
import type { Context } from 'react';
import { createContext, use, useRef, useState } from 'react';
import { ConnectedMarketTradeForm } from '@/components/market/connected-market-trade-form';
import { MobileTradeCta, MobileTradeSheetShell } from '@/components/market/mobile-trade-shell';
import { DirectTokenPurchaseAction } from '@/components/onramp/direct-purchase/direct-token-purchase-action';
import { useChartSettingsStore } from '@/stores/chart-settings';

// ── Types ────────────────────────────────────────────────────────────────────

export interface FloatingTradeButtonOutcome {
  index: number;
  title: string;
  mint: string;
  /** Optional outcome image URL from backend. */
  imageUrl?: string | undefined;
  /** Whether the outcome image is flagged NSFW. */
  imageNsfw?: boolean | undefined;
}
interface MarketMobileTradeAnchorContextValue {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  openTradeForm: () => void;
  disabled: boolean;
  marketPubkey: string;
  selectedOutcomeIndex: number | null;
}
const MarketMobileTradeAnchorContext: Context<MarketMobileTradeAnchorContextValue | null> = createContext<MarketMobileTradeAnchorContextValue | null>(null);
function useMarketMobileTradeAnchorContext(): MarketMobileTradeAnchorContextValue {
  const v = use(MarketMobileTradeAnchorContext);
  if (v === null) {
    throw new Error('MarketMobileTradeStickyAnchor must be used inside MarketMobileTradeRoot');
  }
  return v;
}
export function useOpenMarketMobileTradeForm(): (() => void) | undefined {
  return use(MarketMobileTradeAnchorContext)?.openTradeForm;
}
export interface MarketMobileTradeRootProps {
  enabled: boolean;
  marketPubkey: string;
  /** Market display name — forwarded to the realised-PnL sell share text. */
  marketTitle?: string;
  outcomes: FloatingTradeButtonOutcome[];
  marketStatus: string;
  /** The row the page holds during a landing resolution (see `ConnectedMarketTradeForm`). */
  heldMarket?: GetMarketRow | undefined;
  hasHoldings?: boolean;
  onOutcomeChange?: (index: number) => void;
  children: React.ReactNode;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isButtonDisabled(marketStatus: string): boolean {
  // Trading is only permitted while the market is in the tradeable
  // (`trading`) state — every other lifecycle state disables the CTA.
  return !isMarketTradeable(marketStatus);
}
function isValidTradeFormStatus(status: string): status is MarketStatusString {
  return isValidMarketStatus(status);
}

// ── Fixed bottom trade CTA (mobile / tablet) ────────────────────────────────

export function MarketMobileTradeStickyAnchor() {
  const $ = _c(9);
  const {
    anchorRef,
    openTradeForm,
    disabled,
    marketPubkey,
    selectedOutcomeIndex
  } = useMarketMobileTradeAnchorContext();
  let t0;
  if ($[0] !== disabled || $[1] !== marketPubkey || $[2] !== selectedOutcomeIndex) {
    t0 = disabled ? undefined : <DirectTokenPurchaseAction placement="responsive" marketAddress={marketPubkey} outcomeIndex={selectedOutcomeIndex} origin="market" />;
    $[0] = disabled;
    $[1] = marketPubkey;
    $[2] = selectedOutcomeIndex;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== anchorRef || $[5] !== disabled || $[6] !== openTradeForm || $[7] !== t0) {
    t1 = <MobileTradeCta purchaseAction={t0} onOpen={openTradeForm} disabled={disabled} anchorRef={anchorRef} testIdPrefix="mobile-trade-inline" />;
    $[4] = anchorRef;
    $[5] = disabled;
    $[6] = openTradeForm;
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}

// ── Root: trade sheet ────────────────────────────────────────────────────────

export function MarketMobileTradeRoot(t0) {
  const $ = _c(26);
  const {
    enabled,
    marketPubkey,
    marketTitle,
    outcomes,
    marketStatus,
    heldMarket,
    hasHoldings,
    onOutcomeChange,
    children
  } = t0;
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = state => state.selectedOutcome[marketPubkey] ?? null;
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedOutcomeIndex = useChartSettingsStore(t1);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  let t2;
  if ($[2] !== outcomes) {
    t2 = outcomes.map(_temp);
    $[2] = outcomes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tradeFormOutcomes = t2;
  let t3;
  if ($[4] !== marketStatus) {
    t3 = isButtonDisabled(marketStatus);
    $[4] = marketStatus;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const disabled = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(true);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== disabled || $[8] !== marketPubkey || $[9] !== selectedOutcomeIndex) {
    t5 = {
      anchorRef,
      marketPubkey,
      selectedOutcomeIndex,
      openTradeForm: t4,
      disabled
    };
    $[7] = disabled;
    $[8] = marketPubkey;
    $[9] = selectedOutcomeIndex;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const anchorContext = t5;
  const t6 = enabled ? anchorContext : null;
  let t7;
  if ($[11] !== enabled || $[12] !== hasHoldings || $[13] !== heldMarket || $[14] !== marketPubkey || $[15] !== marketStatus || $[16] !== marketTitle || $[17] !== onOutcomeChange || $[18] !== open || $[19] !== selectedOutcomeIndex || $[20] !== tradeFormOutcomes) {
    t7 = enabled ? <MobileTradeSheetShell open={open} onOpenChange={setOpen}><ConnectedMarketTradeForm bare={true} marketAddress={marketPubkey} outcomes={tradeFormOutcomes} hasHoldings={hasHoldings ?? false} {...marketTitle !== undefined ? {
        marketTitle
      } : {}} {...selectedOutcomeIndex !== null ? {
        selectedOutcomeIndex
      } : {}} {...isValidTradeFormStatus(marketStatus) ? {
        marketStatus
      } : {}} heldMarket={heldMarket} {...onOutcomeChange !== undefined ? {
        onOutcomeChange
      } : {}} /></MobileTradeSheetShell> : null;
    $[11] = enabled;
    $[12] = hasHoldings;
    $[13] = heldMarket;
    $[14] = marketPubkey;
    $[15] = marketStatus;
    $[16] = marketTitle;
    $[17] = onOutcomeChange;
    $[18] = open;
    $[19] = selectedOutcomeIndex;
    $[20] = tradeFormOutcomes;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== children || $[23] !== t6 || $[24] !== t7) {
    t8 = <MarketMobileTradeAnchorContext.Provider value={t6}>{children}{t7}</MarketMobileTradeAnchorContext.Provider>;
    $[22] = children;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
function _temp(o) {
  return {
    index: o.index,
    label: o.title,
    mint: o.mint,
    imageUrl: o.imageUrl,
    imageNsfw: o.imageNsfw === true
  };
}